import { appBuildInfo } from './build-info.js'
import { reportError } from './monitoring.js'

/**
 * Регистрация service worker (D9) и уведомление об обновлении.
 *
 * Сам worker лежит в public/sw.js обычным JavaScript: его собирает
 * не Vite, а браузер, и область действия у него — корень сайта.
 *
 * Версия сборки уходит в адрес регистрации (`/sw.js?v=…`). Иначе файл
 * от сборки к сборке байт в байт одинаков, браузер не видит изменений
 * и нового worker'а не устанавливает — а вместе с ним не обновляется
 * и содержимое кэша. Тот же параметр worker кладёт в имя кэша, поэтому
 * чужие версии удаляются при активации.
 */

/** Адрес регистрации: версия и хеш сборки, больше ничего. */
export function serviceWorkerUrl(version: string, commit: string): string {
  return `/sw.js?v=${encodeURIComponent(`${version}-${commit}`)}`
}

export type UpdateListener = () => void

/**
 * Появился ли уже установленный worker, готовый заменить текущий.
 *
 * Условие с `controller` важно: при первом заходе worker тоже
 * оказывается `installed`, но заменять ему нечего, и предлагать
 * перезагрузку было бы враньём.
 */
function watchForUpdate(
  registration: ServiceWorkerRegistration,
  container: Pick<ServiceWorkerContainer, 'controller'>,
  onUpdate: UpdateListener,
): void {
  const announce = (worker: ServiceWorker | null) => {
    if (worker === null) return
    const check = () => {
      if (worker.state === 'installed' && container.controller !== null) onUpdate()
    }
    check()
    worker.addEventListener('statechange', check)
  }

  announce(registration.waiting)
  registration.addEventListener('updatefound', () => {
    announce(registration.installing)
  })
}

export async function registerServiceWorker(
  onUpdate?: UpdateListener,
): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null

  const build = appBuildInfo()

  try {
    const registration = await navigator.serviceWorker.register(
      serviceWorkerUrl(build.version, build.commit),
      { scope: '/' },
    )
    if (onUpdate !== undefined) {
      watchForUpdate(registration, navigator.serviceWorker, onUpdate)
    }
    return registration
  } catch (error) {
    // Отсутствие офлайна — не повод не запускать приложение:
    // без service worker оно работает, просто требует сети.
    reportError(error, { scope: 'service-worker-registration' })
    return null
  }
}
